package com.shadowings.gero.presentation.viewmodel

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.lifecycle.SavedStateHandle
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.shadowings.gero.data.lessons.DistractorPool
import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.data.repository.SentenceRepository
import com.shadowings.gero.domain.model.CalendarPromptScreen
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.FeaturePollOption
import com.shadowings.gero.domain.model.FeaturePollScreen
import com.shadowings.gero.domain.model.FinalQuizScreen
import com.shadowings.gero.domain.model.ICountableQuiz
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.ReviewPromptScreen
import com.shadowings.gero.domain.model.StreakCelebrationScreen
import com.shadowings.gero.domain.model.WrongAnswerRecord
import com.shadowings.gero.domain.model.buildCorrectionQuizContent
import com.shadowings.gero.domain.model.correctable
import com.shadowings.gero.domain.model.finalQuizCharacterBlacklist
import com.shadowings.gero.domain.repository.AnalyticsRepository
import com.shadowings.gero.domain.repository.DatabaseRepository
import com.shadowings.gero.domain.repository.LessonContentRepository
import com.shadowings.gero.domain.repository.UserDataRepository
import com.shadowings.gero.domain.usecase.RemoveHeartUseCase
import com.shadowings.gero.domain.usecase.ScheduleQuizReminderUseCase
import com.shadowings.gero.getPlatform
import com.shadowings.gero.presentation.model.QuizEffect
import com.shadowings.gero.presentation.model.QuizUiState
import com.shadowings.gero.presentation.widget.notifyStrikeWidgetDataChanged
import com.shadowings.gero.util.calculateStreak
import com.shadowings.gero.util.currentDateAsString
import com.shadowings.gero.util.currentTimeMillis
import com.shadowings.gero.util.isCalendarIntegrationSupported
import com.shadowings.gero.util.isDebugBuild
import com.shadowings.gero.util.systemLanguageCode
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.withLock

class QuizViewModel(
    savedStateHandle: SavedStateHandle,
    private val lessonContentRepository: LessonContentRepository,
    private val userDataRepository: UserDataRepository,
    private val removeHeartUseCase: RemoveHeartUseCase,
    private val databaseRepository: DatabaseRepository,
    private val analyticsRepository: AnalyticsRepository,
    private val scheduleQuizReminderUseCase: ScheduleQuizReminderUseCase,
    private val sentenceRepository: SentenceRepository,
) : ViewModel() {

    val lessonId: String = savedStateHandle.get<String>("lessonId") ?: ""

    private val _quizList = MutableStateFlow<List<IQuizContent>>(emptyList())

    val quizListState: MutableState<List<IQuizContent>> = mutableStateOf(emptyList())

    // Correction mode only: every reconstructed IQuizContent's own (unique, per-render) id maps
    // to the *original* lessonId of the WrongAnswerRecord it was rebuilt from. QuizScreen needs
    // this to pass the right lessonId per page — a single session-wide lessonId (as every other
    // quiz uses) can't work here, since one correction batch mixes mistakes from several
    // different lessons, and WrongAnswerRecord.buildId(lessonId, contentKey) must reproduce the
    // *original* id for resolveWrongAnswer/recordWrongAnswer to hit the right saved record.
    val correctionLessonIdByContentId: MutableState<Map<String, String>> = mutableStateOf(emptyMap())

    val quizResultsState: MutableState<Map<String, Boolean>> = mutableStateOf(mapOf())
    val comboCount: MutableState<Int> = mutableStateOf(0)
    val errorsCount: MutableState<Int> = mutableStateOf(0)
    val comboFiveRewards: MutableState<Int> = mutableStateOf(0)
    val correctAnswersCount: MutableState<Int> = mutableStateOf(0)

    init {
        analyticsRepository.logStartQuiz(lessonId)
        viewModelScope.launch(Dispatchers.Default) {
            databaseRepository.isReady.first { it }
            val userData = userDataRepository.userData.first()
            val hieros = databaseRepository.hieros
            val words = databaseRepository.words
            val verbs = databaseRepository.verbs
            val languageCode = userData.language.ifBlank { systemLanguageCode() }

            // Decided once, up front, instead of on the fly when "Fine" is tapped: the streak/
            // review pages are just regular pages in this list from the start, so advancing the
            // pager never has to append to it and race the pager's layout for a page that didn't
            // exist a moment ago (see git history — that raced and needed two taps to advance).
            val todayStr = currentDateAsString()
            val isFirstOfToday = !userData.completedQuizDates.contains(todayStr)
            // Reuse calculateStreak instead of re-deriving "streak after today" by hand — a
            // hand-rolled isConsecutiveDay(lastQuizDate, today) check breaks when today's quiz
            // was already completed earlier (lastQuizDate == today, a 0-day gap, not a 1-day
            // "consecutive" one), wrongly resetting the shown count to 1. Adding today to the
            // dates list is a no-op if it's already there, so this is correct either way.
            val newStrike = calculateStreak(userData.completedQuizDates + todayStr, todayStr)
            // No app store to review from on web — getRateAppUrl() falls back to the Play
            // Store there, which would be a confusing destination for a web visitor.
            val eligibleForReview = !getPlatform().isWeb &&
                    ((!userData.reviewPromptDone && newStrike >= 2))

            // Local-only, not synced via UserData/cloud sync (see UserDataRepository.calendarPromptDone/
            // calendarPromptDismissedAt) — same reasoning as reminderEnabled/Hour/Minute. Re-prompt policy:
            // never shown -> show; dismissed once -> wait 5 days, show again; dismissed twice -> never again.
            val calendarPromptDone = userDataRepository.calendarPromptDone.first()
            val calendarPromptDismissedAt = userDataRepository.calendarPromptDismissedAt.first()
            val daysSinceDismissed = if (calendarPromptDismissedAt > 0) {
                (currentTimeMillis() - calendarPromptDismissedAt) / 86_400_000L
            } else {
                null
            }

            val eligibleForCalendarPrompt = isCalendarIntegrationSupported() &&
                    ((!calendarPromptDone &&
                            (calendarPromptDismissedAt == 0L || (daysSinceDismissed ?: 0) >= 5)))

            val eligibleForFeaturePoll = userData.answeredFeaturePolls[FeaturePollOption.POLL_KEY] != true

            // Baseline follows the same unlock flags as CharacterScreen; a per-lesson blacklist
            // (see FinalQuizCharacterBlacklist.kt) can still exclude a character who is
            // narratively absent even though already unlocked.
            val unlockedCharacters = buildList {
                add(Characters.Alyn)
                add(Characters.Nilo)
                if (userData.explanations.contains(LessonIds.END_VERB_1)) add(Characters.Zeta)
                if (userData.explanations.contains(LessonIds.ENDING_VSO)) add(Characters.Masko)
            }
            val blacklistedCharacters = finalQuizCharacterBlacklist[lessonId].orEmpty()
            val availableCharacters = (unlockedCharacters - blacklistedCharacters)
                .ifEmpty { listOf(Characters.Alyn) }

            val verbSentences = sentenceRepository.getSeenFilteredVerbSentences()
            val distractorPool = sentenceRepository.getDistractorPool(lessonId)
            // Feature poll disabled: its "Skip" button (QuizScreen.kt's onDismiss for
            // FeaturePollScreen) just calls advanceOrFinish and never persists an
            // answered/dismissed flag, so eligibleForFeaturePoll stays true forever after a
            // skip and the poll reappeared on every quiz completion instead of once. Commented
            // out pending a decision on the intended re-prompt policy (see e.g. the
            // never/5-day/never CalendarPromptScreen policy above for a possible model).
            // Split off from the other extra pages: the streak counter is bumped in
            // onQuizCompleted regardless of lessonId (a correction drill still counts as the
            // day's activity), so the celebration page must be able to appear for a correction
            // drill too — otherwise the streak increments silently with no page acknowledging it,
            // and real lesson quizzes completed later that day also miss it since isFirstOfToday
            // is already false by then.
            val streakPages: List<IQuizContent> = if (isFirstOfToday) {
                listOf(StreakCelebrationScreen(strikeCount = newStrike))
            } else {
                emptyList()
            }
            val extraPages = streakPages + buildList {
                if (isFirstOfToday) {
                    if (eligibleForReview) add(ReviewPromptScreen())
                    if (eligibleForCalendarPrompt) add(CalendarPromptScreen())
                }
            }
            // .ifEmpty {
            //     if (eligibleForFeaturePoll) listOf(FeaturePollScreen()) else emptyList()
            // }
            val list = if (lessonId == LessonIds.CORRECTION) {
                // Synthetic quiz built from saved mistakes, not real lesson content — see
                // buildCorrectionQuizContent. Review/calendar/feature-poll pages are tied to
                // lesson-completion semantics that don't apply here, but the streak page does
                // (see streakPages comment above).
                val correctionRecords = userData.wrongAnswers.correctable()
                    .shuffled()
                    .take(WrongAnswerRecord.CORRECTION_BATCH_SIZE)
                val correctionContent = buildCorrectionQuizContent(correctionRecords)
                correctionLessonIdByContentId.value = correctionRecords.zip(correctionContent)
                    .associate { (record, content) -> (content as ICountableQuiz).id to record.lessonId }
                // Always Alyn here: the final line is a fixed "N mistakes corrected" text rather
                // than the per-character stars feedback (see FinalQuizScreenComposable).
                correctionContent + listOf(FinalQuizScreen(availableCharacters = listOf(Characters.Alyn))) + streakPages
            } else {
                DistractorPool.buildLock.withLock {
                    lessonContentRepository.getQuizContent(
                        lessonId,
                        hieros,
                        words,
                        verbs,
                        languageCode,
                        verbSentences,
                        distractorPool
                    )
                } + listOf(FinalQuizScreen(availableCharacters = availableCharacters)) + extraPages
            }
            _quizList.value = list
            quizListState.value = list
        }
    }

    private val _effects = Channel<QuizEffect>(Channel.BUFFERED)
    val effects = _effects.receiveAsFlow()

    val uiState: StateFlow<QuizUiState> = combine(
        userDataRepository.userData,
        userDataRepository.testMode,
        userDataRepository.hideOptions,
        _quizList
    ) { userData, testMode, hideOptions, quizList ->
        QuizUiState(
            userData = userData,
            isTestMode = testMode,
            hideOptions = hideOptions,
            quizList = quizList,
            lessonId = lessonId
        )
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), QuizUiState())

    val hieros: List<Hiero> = databaseRepository.hieros

    fun removeHearts(isCorrect: Boolean) =
        viewModelScope.launch(Dispatchers.Default) { removeHeartUseCase(isCorrect) }

    // "Review your mistakes": recordWrongAnswer upserts a missed question (bumping `attempts`
    // if already present); resolveWrongAnswer removes it once answered correctly again.
    fun recordWrongAnswer(record: WrongAnswerRecord) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.recordWrongAnswer(record)
        }
    }

    fun resolveWrongAnswer(id: String) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.removeWrongAnswer(id)
        }
    }

    fun addExperience(amount: Int) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.addExperience(amount)
        }
    }

    fun grantStreakRestorationTokensUpTo(level: Int) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.ensureStreakRestorationTokensUpTo(level)
        }
    }

    fun redeemStreakRestoration(dateStr: String) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.redeemStreakRestorationToken(dateStr)
        }
    }

    fun acknowledgeStreakRestorationBadge() {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setStreakRestorationBadgeSeen(true)
        }
    }

    // Checked and set synchronously on the caller's (main) thread, before launching: the
    // isFirstOfToday check inside the coroutine can't guard a second call on its own, since both
    // coroutines can read userData before either records today's date (+20 XP, duplicate analytics).
    private var quizCompleted = false

    fun onQuizCompleted(stars: Int, total: Int, errors: Int) {
        if (quizCompleted) return
        quizCompleted = true
        viewModelScope.launch(Dispatchers.Default) {
            val todayStr = com.shadowings.gero.util.currentDateAsString()
            val userData = userDataRepository.userData.first()
            val isFirstOfToday = !userData.completedQuizDates.contains(todayStr)

            // A correction drill isn't a real lesson: it must not persist stars (they'd be stored
            // under LessonIds.CORRECTION and count towards star-gated lesson unlocking).
            if (lessonId != LessonIds.CORRECTION) {
                userDataRepository.setStar(lessonId, stars)
            }
            analyticsRepository.logQuizCompleted(lessonId, stars, total, errors)

            if (isFirstOfToday) {
                userDataRepository.recordQuizCompletedDate(todayStr)
                // Persisted here, not by the screen: in correction mode the screen can pop before
                // it collects the effect, and the bonus was lost. Tokens for a level crossed by the
                // bonus are granted here too for the same reason (idempotent, so the screen's own
                // grant on the level-up page is harmless).
                userDataRepository.addExperience(DAILY_QUIZ_XP_BONUS)
                val experience = userDataRepository.userData.first().experience
                userDataRepository.ensureStreakRestorationTokensUpTo(
                    com.shadowings.gero.domain.model.GameConstants.getPlayerLevel(experience)
                )
                _effects.send(QuizEffect.ExperienceGranted(DAILY_QUIZ_XP_BONUS))
            }

            scheduleQuizReminderUseCase(forceTomorrow = true)
            notifyStrikeWidgetDataChanged()
        }
    }

    fun onReviewPromptAccepted() {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setReviewPromptDone(true)
        }
    }

    fun onCalendarPromptAccepted() {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setCalendarPromptDone(true)
        }
    }

    fun onCalendarPromptDismissed() {
        viewModelScope.launch(Dispatchers.Default) {
            val alreadyDismissedOnce = userDataRepository.calendarPromptDismissedAt.first() > 0L
            if (alreadyDismissedOnce) {
                userDataRepository.setCalendarPromptDone(true)
            } else {
                userDataRepository.setCalendarPromptDismissedAt(currentTimeMillis())
            }
        }
    }

    fun onFeaturePollSubmitted(option: FeaturePollOption, freeText: String) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setFeaturePollAnswered(FeaturePollOption.POLL_KEY, true)
            analyticsRepository.logFeaturePollResponse(option.analyticsKey, freeText)
        }
    }

    fun logStatClicked(statType: String) {
        analyticsRepository.logStatClicked(statType)
    }

    fun onBackClicked() {
        viewModelScope.launch {
            _effects.send(QuizEffect.NavigateBack)
        }
    }

    companion object {
        const val DAILY_QUIZ_XP_BONUS = 10
    }
}
